// Reading an image a customer picked for a document.
//
// The layout engine skips any image it cannot draw (layoutEngine drawImage),
// so a corrupt file, or one in a format jsPDF cannot place, would leave the
// finished document quietly missing its logo or signature. Every upload goes
// through here instead: the bytes must decode, and anything the engine cannot
// draw is re-encoded to PNG so it always lands on the page.

export const MAX_IMAGE_BYTES = 1_500_000;

// what jsPDF places directly; everything else is re-encoded below
const DRAWABLE = /^image\/(png|jpe?g)$/i;
const mb = (bytes) => String(+(bytes / 1_000_000).toFixed(1)).replace(/\.0$/, "");

const decode = (dataUrl) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => ((img.naturalWidth && img.naturalHeight) ? resolve(img) : reject(new Error("empty")));
  img.onerror = () => reject(new Error("undecodable"));
  img.src = dataUrl;
});

const toPng = (img) => {
  const c = document.createElement("canvas");
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext("2d").drawImage(img, 0, 0);
  return c.toDataURL("image/png");
};

// Resolves to a data URL the document can definitely draw; rejects with a
// message worded for a toast.
export async function readImageFile(file, { maxBytes = MAX_IMAGE_BYTES, label = "Image" } = {}) {
  const what = label.toLowerCase();
  if (!file) throw new Error(`Choose a ${what} first`);
  if (!String(file.type || "").startsWith("image/")) throw new Error(`${label} must be an image file`);
  if (file.size > maxBytes) throw new Error(`${label} must be under ${mb(maxBytes)} MB`);

  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(new Error("Could not read that file. Try a different one."));
    r.onloadend = () => resolve(String(r.result || ""));
    r.readAsDataURL(file);
  });
  if (!dataUrl.startsWith("data:image/")) throw new Error(`${label} must be an image file`);

  let img;
  try { img = await decode(dataUrl); }
  catch { throw new Error(`That ${what} could not be opened. Try a different file.`); }

  if (DRAWABLE.test(file.type)) return dataUrl;
  // SVG, WebP, HEIC and friends: the browser decoded it, the PDF cannot place
  // it, so hand on a PNG of the same pixels
  try {
    const png = toPng(img);
    if (png.length > maxBytes * 2) throw new Error("too big once converted");
    return png;
  } catch {
    throw new Error(`That ${what} is in a format documents cannot use. Save it as a PNG or JPG and try again.`);
  }
}
